/**
 * Suppliers on a project (2026-09-25, Callum: "suppliers on projects").
 *
 * One list per project: who, which of the project's parts they cover, where it stands
 * (considering → asked → quoted → approved / declined), what they quoted and how long
 * they take. Every change saves the WHOLE list at once (PATCH `suppliers`), exactly like
 * the project's accounts — there is no separate Save to forget.
 *
 * 🔴 Quoted cost is COST: the field exists only when the server says this person may see
 * margin (`margin_visible`). Somebody without it still edits status and parts, and the
 * server keeps the cost they cannot see (`work_items._suppliers`).
 * The mirror has no supplier master, so names come from receipts; a supplier QX does not
 * know is allowed (a bid often starts with somebody new) and says so.
 */
import { debounce, escapeHtml, getIcon, showError } from '@isg/ui-kit';
import { salesApi } from '../services/sales-api.js';
import { shortDate } from '../lib/fig.js';

export const SUPPLIER_STATUSES = [
  ['considering', 'Considering', 'gray'], ['asked', 'Asked', 'blue'], ['quoted', 'Quoted', 'blue'],
  ['approved', 'Approved', 'green'], ['declined', 'Declined', 'red'],
];
const STATUS = Object.fromEntries(SUPPLIER_STATUSES.map(([id, label, tone]) => [id, { label, tone }]));

// the name a person typed on this project, else purchasing's (a colleague checked it),
// else whatever the receipts carry
const nameOf = (s) => s.supplier_name || (s.directory || {}).name || s.erp_name || null;

/** purchasing's directory facts: contact, lead time, terms — never cost or on-time. */
function directoryLine(d) {
  if (!d) return '';
  const bits = [
    d.contact_name && escapeHtml(d.contact_name),
    d.contact_email && `<a href="mailto:${escapeHtml(d.contact_email)}">${escapeHtml(d.contact_email)}</a>`,
    d.contact_phone && escapeHtml(d.contact_phone),
    d.lead_time_days != null && `usual lead ${escapeHtml(String(d.lead_time_days))} days`,
    d.payment_terms && escapeHtml(d.payment_terms),
    d.moq_note && `MOQ ${escapeHtml(d.moq_note)}`,
    d.location && escapeHtml(d.location),
  ].filter(Boolean);
  return bits.length ? `<div class="sc-proj-sub" title="From purchasing's supplier directory">${getIcon('book-open', 11)} ${bits.join(' · ')}</div>` : '';
}

export function suppliersSection(p, writer) {
  // ⚠️ A version conflict repaints from the bare record (no child lists). Saving from
  // there would REPLACE the suppliers with nothing, so the section refuses to edit.
  if (!Array.isArray(p.suppliers)) {
    return '<div class="form-group"><label class="form-label">Suppliers</label><p class="sc-proj-sub">Close and reopen this project to see its suppliers.</p></div>';
  }
  const rows = p.suppliers;
  const parts = (p.parts || []).map(x => x.part);
  const cost = p.margin_visible;
  return `<div class="form-group"><label class="form-label">Suppliers (${rows.length})</label>
    ${rows.length ? `<div style="overflow-x:auto"><table class="data-table" data-suppliers>
      <thead><tr><th>Supplier</th><th>Covers</th><th>Status</th>${cost ? '<th style="text-align:right">Quoted cost</th>' : ''}
        <th style="text-align:right">Lead (days)</th>${writer ? '<th></th>' : ''}</tr></thead>
      <tbody>${rows.map((s, i) => `<tr data-srow="${i}">
        <td><strong>${escapeHtml(nameOf(s) || s.supplier_code)}</strong>
          ${nameOf(s) ? `<span class="sc-proj-sub">${escapeHtml(s.supplier_code)}</span>` : ''}
          ${s.erp_known === false ? '<span class="badge badge-amber" title="QX has no receipt or purchase order from this code — a new supplier, or a typo">not in QX</span>' : ''}
          ${s.erp_known && !nameOf(s) ? '<span class="sc-proj-sub">no name in the ERP mirror</span>' : ''}
          ${s.last_receipt ? `<div class="sc-proj-sub">last received ${escapeHtml(shortDate(s.last_receipt))}</div>` : ''}
          ${directoryLine(s.directory)}
          ${writer ? `<input class="form-input" data-f="note" maxlength="2000" placeholder="Note" value="${escapeHtml(s.note || '')}" style="margin-top:4px">`
            : (s.note ? `<div class="sc-proj-sub">${escapeHtml(s.note)}</div>` : '')}</td>
        <td>${parts.length ? parts.map(pt => {
            const on = (s.parts || []).includes(pt);
            return writer
              ? `<label class="sc-proj-sub" style="display:flex;gap:4px"><input type="checkbox" data-cover="${escapeHtml(pt)}"${on ? ' checked' : ''}> ${escapeHtml(pt)}</label>`
              : (on ? `<div class="sc-proj-sub">${escapeHtml(pt)}</div>` : '');
          }).join('') : '<span class="sc-proj-sub">No parts on this project yet.</span>'}</td>
        <td>${writer ? `<select class="form-select" data-f="status">${SUPPLIER_STATUSES.map(([id, label]) =>
            `<option value="${id}"${s.status === id ? ' selected' : ''}>${label}</option>`).join('')}</select>`
          : `<span class="badge badge-${STATUS[s.status]?.tone || 'gray'}">${escapeHtml(STATUS[s.status]?.label || s.status)}</span>`}</td>
        ${cost ? `<td style="text-align:right">${writer
          ? `<input class="form-input" data-f="quoted_cost" type="number" min="0" step="0.0001" style="width:110px" value="${s.quoted_cost ?? ''}">
             <input class="form-input" data-f="currency" maxlength="3" placeholder="${escapeHtml(p.currency || 'GBP')}" style="width:60px" value="${escapeHtml(s.currency || '')}">`
          : (s.quoted_cost != null ? `${escapeHtml(String(s.quoted_cost))} ${escapeHtml(s.currency || '')}` : '—')}</td>` : ''}
        <td style="text-align:right">${writer
          ? `<input class="form-input" data-f="lead_time_days" type="number" min="0" step="1" style="width:80px" value="${s.lead_time_days ?? ''}">`
          : (s.lead_time_days ?? '—')}</td>
        ${writer ? `<td><button type="button" class="btn btn-ghost btn-sm" data-sdrop aria-label="Remove supplier">${getIcon('x', 14)}</button></td>` : ''}
      </tr>`).join('')}</tbody></table></div>`
      : '<p class="sc-proj-sub">No suppliers yet. Add who you are asking — and see below who has supplied these parts before.</p>'}
    ${writer ? `<div class="sc-inline-add" style="position:relative">
        <input class="form-input" data-supplier-q placeholder="Add a supplier — code or name…" autocomplete="off" style="flex:1">
      </div><div data-supplier-results></div>` : ''}
    <div data-supplier-suggest style="margin-top:var(--space-2)"></div>
    ${rows.length && p.supplier_directory && p.supplier_directory !== 'ok'
      ? `<p class="sc-proj-sub">${getIcon('alert-circle', 11)} Purchasing's supplier directory: ${escapeHtml(p.supplier_directory)}</p>` : ''}
    ${cost ? '<p class="sc-proj-sub">Quoted cost is what the supplier quoted US, per unit — only people who can see margin see it.</p>' : ''}
  </div>`;
}

/** Wire the section. `patch(body, message)` is the sheet's own save. */
export function wireSuppliers(root, item, { writer, patch }) {
  if (!Array.isArray(item.suppliers)) return;
  const current = () => (item.suppliers || []).map(s => ({
    supplier_code: s.supplier_code, supplier_name: s.supplier_name || undefined, parts: s.parts || [],
    status: s.status, lead_time_days: s.lead_time_days ?? undefined, note: s.note || undefined,
    ...(item.margin_visible ? { quoted_cost: s.quoted_cost ?? undefined, currency: s.currency || undefined } : {}),
  }));
  const save = (list, msg) => patch({ suppliers: list }, msg);

  loadSuggestions(root, item, writer, (s) => {
    const list = current();
    if (list.some(x => x.supplier_code === s.supplier_code)) return;
    list.push({ supplier_code: s.supplier_code, supplier_name: s.name || undefined,
      parts: (s.covers || []).map(c => c.part), status: 'considering' });
    save(list, 'Supplier added.');
  });
  if (!writer) return;

  const table = root.querySelector('[data-suppliers]');
  if (table) {
    table.addEventListener('change', (e) => {
      const tr = e.target.closest('[data-srow]');
      if (!tr) return;
      const list = current();
      const row = list[Number(tr.dataset.srow)];
      if (e.target.dataset.cover !== undefined) {
        const pt = e.target.dataset.cover;
        row.parts = e.target.checked ? [...new Set([...row.parts, pt])] : row.parts.filter(x => x !== pt);
      } else {
        const f = e.target.dataset.f;
        const v = e.target.value.trim();
        row[f] = v === '' ? undefined : (f === 'currency' ? v.toUpperCase() : v);
      }
      save(list, 'Saved.');
    });
    table.addEventListener('click', (e) => {
      const b = e.target.closest('[data-sdrop]');
      if (!b) return;
      const list = current();
      list.splice(Number(b.closest('[data-srow]').dataset.srow), 1);
      save(list, 'Supplier removed.');
    });
  }

  // type-ahead: the estate's 2-character / 300 ms / sequence-guard pattern (components/find.js)
  const box = root.querySelector('[data-supplier-q]');
  const out = root.querySelector('[data-supplier-results]');
  let seq = 0;
  const run = debounce(async () => {
    const q = box.value.trim();
    const mine = ++seq;
    if (q.length < 2) { out.innerHTML = ''; return; }
    try {
      const items = ((await salesApi.searchSuppliers(item.entity, q)).data || {}).items || [];
      if (mine !== seq) return;
      out.innerHTML = `<div class="card sc-list" style="margin-top:4px">${items.map((s, i) => `
          <div class="sc-list-item global-search-result" data-pick="${i}" role="button" tabindex="-1"><div class="sc-list-body">
            <strong>${escapeHtml(s.name || s.supplier_code)}</strong> <span class="sc-proj-sub">${escapeHtml(s.supplier_code)}${s.last_receipt ? ` · last received ${escapeHtml(shortDate(s.last_receipt))}` : ''}</span>
          </div></div>`).join('')}
          <div class="sc-list-item global-search-result" data-pick="new" role="button" tabindex="-1"><div class="sc-list-body">
            ${getIcon('plus', 12)} Add <strong>${escapeHtml(q)}</strong> as a supplier QX does not know yet</div></div></div>`;
      out.onclick = (e) => {
        const el = e.target.closest('[data-pick]');
        if (!el) return;
        const s = el.dataset.pick === 'new' ? { supplier_code: q.slice(0, 64), name: null } : items[Number(el.dataset.pick)];
        const list = current();
        if (list.some(x => x.supplier_code === s.supplier_code)) { showError('That supplier is already on the project.'); return; }
        list.push({ supplier_code: s.supplier_code, supplier_name: s.name || undefined, parts: [], status: 'considering' });
        save(list, 'Supplier added.');
      };
    } catch (err) {
      if (mine === seq) out.innerHTML = `<p class="sc-proj-sub">Could not search suppliers: ${escapeHtml(err.message || '')}</p>`;
    }
  }, 300);
  box?.addEventListener('input', run);
}

async function loadSuggestions(root, item, writer, add) {
  const box = root.querySelector('[data-supplier-suggest]');
  if (!box || !(item.parts || []).length) return;
  box.innerHTML = '<p class="sc-proj-sub">Looking for who has supplied these parts…</p>';
  try {
    const d = (await salesApi.getSupplierSuggestions(item.id)).data || {};
    const rows = (d.items || []).filter(s => !s.on_item);
    box.innerHTML = rows.length ? `<div class="sc-proj-sub" style="margin-bottom:4px">${getIcon('truck', 12)}
        Who has supplied these parts (receipts in 3 years, open purchase orders):</div>
      <ul class="sc-proj-list">${rows.slice(0, 8).map((s, i) => `<li>
        <strong>${escapeHtml(s.name || s.supplier_code)}</strong>
        <span class="sc-proj-sub">${escapeHtml(s.supplier_code)} · covers ${s.covers.length} of ${(item.parts || []).length}
          (${escapeHtml(s.covers.map(c => c.part + (c.their_part ? ` as ${c.their_part}` : '')).join(', '))})
          ${s.receipts ? ` · ${s.receipts} receipt${s.receipts === 1 ? '' : 's'}, last ${escapeHtml(shortDate(s.last_receipt))}` : ''}
          ${s.open_po_lines ? ` · ${s.open_po_lines} open PO line${s.open_po_lines === 1 ? '' : 's'}` : ''}</span>
        ${directoryLine(s.directory)}
        ${writer ? `<button type="button" class="btn btn-ghost btn-sm" data-suggest-add="${i}">${getIcon('plus', 12)} Add</button>` : ''}
      </li>`).join('')}</ul>
      ${(d.unplaced || []).length ? `<p class="sc-proj-sub">Not looked up (not our part number): ${escapeHtml(d.unplaced.join(', '))}</p>` : ''}`
      : `<p class="sc-proj-sub">${(d.items || []).length ? 'Everyone who has supplied these parts is already on the list.'
        : 'Nobody has supplied these parts to us in the last three years — a new source is needed.'}</p>`;
    box.querySelectorAll('[data-suggest-add]').forEach(b => b.addEventListener('click', () => add(rows[Number(b.dataset.suggestAdd)])));
  } catch (err) {
    box.innerHTML = `<p class="sc-proj-sub">Supplier history could not be checked just now: ${escapeHtml(err.message || '')}</p>`;
  }
}
